// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Platform analytics.
 *
 * Metric labels are shown as the platform reported them. `DAT-012` prohibits
 * sensitive or high-cardinality identifiers in telemetry, so this screen
 * renders labels verbatim rather than joining them against any subject record.
 */

import { capabilities, queryKeys, type ValidatedMetrics } from '@eai/ui-shared';
import type { ReactNode } from 'react';

import { ResourceScreen, useControlPlaneQuery } from '../app-shell';

/** Format a metric value with its unit. */
export function formatMetric(row: ValidatedMetrics): string {
  const value = new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 }).format(row.value);
  return row.unit === null ? value : `${value} ${row.unit}`;
}

/** Platform analytics screen. */
export function AnalyticsScreen(): ReactNode {
  const query = useControlPlaneQuery(
    (scope) => queryKeys.controlPlane.metrics(scope),
    (client, signal) => client.listMetrics({ signal }),
  );

  return (
    <ResourceScreen<ValidatedMetrics>
      title="Analytics"
      description="Platform metric samples for the current scope. Values are point observations, not service level objectives."
      requiredCapability={capabilities.analyticsRead}
      area="Analytics"
      query={query}
      rowKey={(row) => `${row.name}:${row.recorded_at ?? 'unrecorded'}`}
      emptyTitle="No metric was reported"
      emptyDescription="Telemetry may be unavailable for this period; an absent metric is not a zero measurement."
      columns={[
        { key: 'name', header: 'Metric', render: (row) => row.name },
        { key: 'value', header: 'Value', numeric: true, render: formatMetric },
        { key: 'recorded', header: 'Recorded', render: (row) => row.recorded_at ?? 'Not reported' },
        {
          key: 'labels',
          header: 'Labels',
          render: (row) => {
            const entries = Object.entries(row.labels);
            return entries.length === 0
              ? 'None'
              : entries.map(([key, value]) => `${key}=${value}`).join(', ');
          },
        },
      ]}
    />
  );
}
